import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Settings, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

export const UserFeatureSelector = ({ isOpen, onClose, onSave }) => {
  const { i18n } = useTranslation();
  const [availableFeatures, setAvailableFeatures] = useState([]);
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const getText = (textObj) => {
    if (!textObj) return '';
    if (typeof textObj === 'string') return textObj;
    const currentLang = i18n.language?.startsWith('de') ? 'de' : 'en';
    return textObj[currentLang] || textObj.de || textObj.en || '';
  };

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch available features
      const availableResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/user/homepage-prefs/available-features`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (availableResponse.ok) {
        const availableData = await availableResponse.json();
        setAvailableFeatures(availableData.features);
      }
      
      // Fetch user's current selection
      const prefsResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/user/homepage-prefs`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (prefsResponse.ok) {
        const prefsData = await prefsResponse.json();
        setSelectedFeatures(prefsData.selected_features || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Fehler beim Laden der Features');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleFeature = (featureId) => {
    setSelectedFeatures(prev => {
      if (prev.includes(featureId)) {
        return prev.filter(id => id !== featureId);
      } else {
        if (prev.length >= 6) {
          toast.error('Maximal 6 Features können ausgewählt werden');
          return prev;
        }
        return [...prev, featureId];
      }
    });
  };

  const handleSave = async () => {
    if (selectedFeatures.length === 0) {
      toast.error('Bitte wähle mindestens ein Feature aus');
      return;
    }

    setIsSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/user/homepage-prefs`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ selected_features: selectedFeatures })
        }
      );

      if (response.ok) {
        toast.success('✅ Deine Feature-Auswahl wurde gespeichert!');
        onSave(selectedFeatures);
        onClose();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Speichern');
      }
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <Settings className="h-8 w-8 text-primary" />
            <div>
              <DialogTitle className="text-2xl">Deine Features anpassen</DialogTitle>
              <DialogDescription className="mt-1">
                Wähle bis zu 6 Features, die auf Deiner Startseite angezeigt werden sollen
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : (
          <div className="py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {availableFeatures.map((feature) => {
                const isSelected = selectedFeatures.includes(feature.id);
                return (
                  <Card
                    key={feature.id}
                    className={`cursor-pointer transition-all ${
                      isSelected
                        ? 'border-primary border-2 bg-primary/5'
                        : 'hover:border-primary/50'
                    }`}
                    onClick={() => toggleFeature(feature.id)}
                  >
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-3xl">{feature.icon}</span>
                          <div>
                            <CardTitle className="text-lg">
                              {getText(feature.title)}
                            </CardTitle>
                          </div>
                        </div>
                        <div className="flex items-center">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleFeature(feature.id)}
                            className="pointer-events-none"
                          />
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <CardDescription>{getText(feature.description)}</CardDescription>
                    </CardContent>
                    {isSelected && (
                      <div className="absolute top-2 right-2 bg-primary text-primary-foreground rounded-full p-1">
                        <Check className="h-4 w-4" />
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>

            <div className="mt-6 p-4 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">
                <strong>{selectedFeatures.length} von 6</strong> Features ausgewählt
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Abbrechen
          </Button>
          <Button onClick={handleSave} disabled={isSaving || isLoading}>
            {isSaving ? 'Speichert...' : '💾 Speichern'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
